import { iterateChildren, IterateNodeCallback } from '@common/prosemirror/model/node-iterate';
import { resolvedPosFind } from '@common/prosemirror/model/resolved-pos';
import { Node, NodeType, ResolvedPos, Slice } from 'prosemirror-model';

export declare type FindNodeCallback = (node: Node, position: number, index: number) => any;

export interface FoundNodeInfo {
  node: Node;
  parent?: Node;
  pos: number;
}

export class NodeMapValue implements FoundNodeInfo {
  node: Node;
  pos: number;
  index: number;
  parentPos: number;
  children: NodeMapValue[];
  constructor(node: Node, pos: number, index: number, parentPos: number) {
    this.node = node;
    this.pos = pos;
    this.index = index;
    this.parentPos = parentPos;
    this.children = [];
  };
}

export function findNode(node: Node, predicate: FindNodeCallback): FoundNodeInfo {
  let foundData: FoundNodeInfo;

  // Node.descendants()'s sibling loop never stops early — returning false only skips a node's own
  // children — so guard here or foundData silently ends up as the LAST match, not the first.
  node.descendants((child: Node, pos: number, parent: Node, index: number) => {
    if (foundData) {
      return false;
    }

    if (predicate(child, pos, index)) {
      foundData = {
        node: child,
        parent: parent,
        pos: pos
      };

      return false;
    }
  });

  return foundData;
}

export function hasNode(node: Node, predicate: FindNodeCallback): boolean {
  return !!findNode(node, predicate);
}

export function hasInlineNode(node: Node): boolean {
  return hasNode(node, child => child.isInline);
}

export function isOrHasInlineNode(node: Node): boolean {
  return node.isInline || hasInlineNode(node);
}

/**
 * Gets whether the node is a block that does not contain child blocks.
 */
export function isLeafBlock(node: Node): boolean {
  return node.isBlock &&
    node.type.name !== 'mcCentralContainer' &&
    (node.inlineContent || node.childCount == 0 ||
      (node.childCount === 1 && node.firstChild.type.name === 'mcCentralContainer'));
}

export function containsInlineNode(from: number, to: number, nodeType: NodeType, doc: Node): boolean {
  let containsAnInlineNode = false;

  doc.nodesBetween(from, to, (node: Node) => {
    containsAnInlineNode = containsAnInlineNode || (node.isInline && node.type === nodeType);

    // Break early now that we know that there is an inline node of the given type
    if (containsAnInlineNode) {
      return false;
    }
  });

  return containsAnInlineNode;
}

/** Returns the data of the first child node with the specified type if found. */
export function getFirstChild(node: NodeMapValue, nodeTypeName: string): NodeMapValue {
  let child: NodeMapValue;

  iterateChildren(node, nodeTypeName, (item) => {
    child = item;
    return false;
  });

  return child;
}

/** Returns the first child node with the specified type if found. */
export function getFirstChildNode(node: Node, nodeType: string): Node {
  const tmp = new NodeMapValue(node, 0, 0, undefined);
  let child = getFirstChild(tmp, nodeType);
  return child?.node;
}

export function getLowestLevelContainer(slice: Slice): Node {
  let content = slice?.content;
  while (content) {
    const nextFragment = content?.firstChild.content;
    // no next fragment || next fragment has more then one child so lowest level container || last container that allows its content to be replaced
    if (!nextFragment?.size || nextFragment.childCount > 1) {
      // return a node
      return content.firstChild;
    } else {
      // loop a fragment
      content = nextFragment;
    }
  }
}

/**
 * Gets the nearest block ancestor node info.
 */
export function closestBlockAncestor($pos: ResolvedPos): FoundNodeInfo | null {
  const found = resolvedPosFind($pos, (node) => node.isBlock);
  if (!found) return null;
  return { node: found.node, pos: found.$nodePos.pos };
}

/**
 * Gets the top most block nodes that the given range contains.
 * That means for example in case: <div><p>te|xt</p></div> this method returns <div> node,
 * but for <div><p>te|xt</p><p>text</p></div> it returns the first <p> node because the range does not have second <p> node,
 * but for <div><p>te|xt</p><p>te|xt</p></div> it returns again the <div> node because the range has both child <p> nodes.
 * @param canPreserveInvalidParent - function which allows to determinate whether the node which is parent for other block nodes
 * but it is not fully selected (only some children are selected) can be preserved in the result. By default such nodes are excluded from the result.
 * @param canPreserveLeaf - function which allows to determinate whether the deepest node can be included into the result node array
 * if its parent is valid for the result. If the function returns true the deepest node will be included into result but its parent will be excluded.
 * if this parameter is not specified such nodes will be excluded from the result leaving only their parents.
 */
export function topBlocksBetween($from: ResolvedPos, $to: ResolvedPos, canPreserveInvalidParent?: IterateNodeCallback, canPreserveLeaf?: IterateNodeCallback): FoundNodeInfo[] {
  const doc = $from.doc;
  const blocks = new Map<number, NodeMapValue>();

  // sort numbers in reverse order
  const descendant = (x: number, y: number): number => {
    return x > y ? -1 : 1;
  };
  // delete the block map entry and its ancestors
  const deleteWithAncestors = (pos: number) => {
    const block = blocks.get(pos);
    if (!block) return;
    blocks.delete(pos);
    deleteWithAncestors(block.parentPos);
  };

  // add <html> node into the map
  blocks.set(0, new NodeMapValue(doc.firstChild, 0, 0, -1));
  // accumulate other selected block nodes into the map
  doc.nodesBetween($from.pos, $to.pos, (node, pos, parent, index) => {
    if (pos === 0 || !node.isBlock) return;
    const $pos = doc.resolve(pos);
    const parentPos = $pos.before($pos.depth);
    const block = new NodeMapValue(node, pos, index, parentPos);
    blocks.set(pos, block);
    blocks.get(parentPos).children.push(block);
  });

  // delete nodes from the map which are not fully selected
  for (let pos of [...blocks.keys()]) {
    const block = blocks.get(pos);
    // leave leaf nodes, they will be handled in the next step
    if (!block || block.children.length == 0) continue;
    // exclude node if not all its children contains selection
    if (block.node.childCount !== block.children.length &&
      // allows preserve the node anyway for special cases
      !canPreserveInvalidParent?.(block.node, blocks, pos, blocks.get(block.parentPos)?.node, block.index)) {
      deleteWithAncestors(pos);
    }
  }

  // delete leaf nodes from the map if its parent is valid for result
  for (let pos of [...blocks.keys()].sort(descendant)) {
    const block = blocks.get(pos);
    if (!block) continue;
    const parent = blocks.get(block.parentPos);
    // parent is invalid, so the leaf node is explicitly valid
    if (!parent) continue;
    // check whether the leaf node can be included into the result
    if (canPreserveLeaf?.(block.node, blocks, pos, parent.node, block.index)) {
      // if yes, delete its ancestors from the map
      deleteWithAncestors(block.parentPos);
    } else {
      // otherwise exclude the leaf node and wait its parent check
      blocks.delete(pos);
    }
  }

  // convert map entries into expected array
  return [...blocks.entries()].map(([key, value]) => {
    return { node: value.node, pos: key };
  });
}

/**
Gets the top most block nodes that the given range contains.
@param predicate - function that allows to exclude deepest block nodes.
if the function returns false then its valid ancestor will be included instead of the ancestor children.
*/
export function deepestBlocksBetween($from: ResolvedPos, $to: ResolvedPos, predicate?: IterateNodeCallback): FoundNodeInfo[] {
  let end: number = 0;
  const doc = $from.doc;
  const deepest: FoundNodeInfo[] = [];
  $from.doc.nodesBetween($from.pos, $to.pos, (node, pos, parent, index) => {
    if (isLeafBlock(node)) {
      let nodeEnd = pos + node.nodeSize;
      // the node ancestor is already selected
      if (end > nodeEnd) return;
      if (!predicate || predicate(node, deepest, pos, parent, index))
        // include the node into the result
        deepest.push({ node, pos });
      else {
        // get the valid node ancestor
        const ancestor = resolvedPosFind(doc.resolve(pos), (a, d, $p) => {
          return predicate(a, deepest, $p.before(d), $p.node(d - 1), $p.index(d));
        });
        // exclude the ancestor descants from the result
        const p = ancestor.$nodePos.pos;
        for (let i = deepest.length - 1; i >= 0; i--) {
          if (deepest[i].pos < p) break;
          deepest.pop();
        }
        // include the ancestor into the result
        deepest.push({ node: ancestor.node, pos: p });
        nodeEnd = p + ancestor.node.nodeSize;
      }
      end = nodeEnd;
    }
  });
  return deepest;
};

/**
 * Gets a node from the given range only if the range points to the node's start and end position, otherwise returns null.
 */
export function rangeNode($start: ResolvedPos, $end: ResolvedPos): Node | null {
  const node = $start.nodeAfter;
  if (!node) {
    return null;
  }

  // get the node start position
  const start = Math.max($start.start($start.depth + 1) - 1, 0);

  // Check the start of the node
  if ($start.pos !== start) {
    return null;
  }

  // Check the end of the node
  if ($end.pos !== start + node.nodeSize) {
    return null;
  }

  return node;
}
